import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Alert,
  StyleSheet,
} from 'react-native';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Header } from '../../components/Header';
import { SlotPicker } from '../../components/SlotPicker';
import { useGetAvailableSlotsQuery, useCreateMeetingMutation } from '../../store/api';
import { useTheme } from '../../theme/ThemeContext';
import { COLORS, getThemeStyles, globalStyles } from '../../theme/designSystem';

const meetingSchema = z.object({
  visitorName: z.string().min(2, 'Visitor name is required'),
  visitorCompany: z.string().optional(),
  visitorPhone: z.string().min(8, 'Phone number must be at least 8 digits'),
  visitorEmail: z.string().email('Please enter a valid email address'),
  meetingPurpose: z.string().min(5, 'Meeting purpose is required (min 5 chars)'),
  meetingDate: z.string().min(10, 'Valid date (YYYY-MM-DD) required'),
  meetingTime: z.string().min(5, 'Please select an available time slot'),
});

type MeetingFormData = z.infer<typeof meetingSchema>;

export const CreateMeetingScreen = ({ route, navigation }: any) => {
  const todayStr = new Date().toISOString().split('T')[0];
  const initialDate = route.params?.initialDate || todayStr;

  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(route.params?.initialTime || null);

  const { data: slotData } = useGetAvailableSlotsQuery(selectedDate);
  const [createMeetingApi, { isLoading }] = useCreateMeetingMutation();
  const { isDark } = useTheme();
  const theme = getThemeStyles(isDark);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<MeetingFormData>({
    resolver: zodResolver(meetingSchema),
    defaultValues: {
      visitorName: '',
      visitorCompany: '',
      visitorPhone: '',
      visitorEmail: '',
      meetingPurpose: '',
      meetingDate: selectedDate,
      meetingTime: selectedSlot || '',
    },
  });

  useEffect(() => {
    setValue('meetingDate', selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    if (selectedSlot) {
      setValue('meetingTime', selectedSlot);
    }
  }, [selectedSlot]);

  const onSubmit = async (data: MeetingFormData) => {
    if (!slotData?.schedule?.id) {
      Alert.alert(
        'Schedule Error',
        'No valid Master Admin work schedule found for this date. Please select a valid schedule date.',
      );
      return;
    }

    try {
      await createMeetingApi({
        ...data,
        scheduleId: slotData.schedule.id,
      }).unwrap();

      Alert.alert(
        'Meeting Created! 🚀',
        'The meeting request has been submitted with PENDING status. Master Admin has been notified for approval.',
        [
          {
            text: 'View Meeting Details',
            onPress: () => navigation.navigate('MeetingHistory'),
          },
        ],
      );
    } catch (err: any) {
      Alert.alert(
        'Submission Failed',
        err?.data?.message || 'Could not create meeting. Check slot capacity and inputs.',
      );
    }
  };

  return (
    <View style={[globalStyles.container, { backgroundColor: theme.bg }]}>
      <Header title="New Meeting Request" subtitle="Submit visitor details for Master Admin authorization" />

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: 32 }}
      >
        <View style={[globalStyles.card, { backgroundColor: theme.cardBg, borderColor: theme.border }]}>
          {/* Visitor Name */}
          <Text style={[styles.label, { color: theme.textPrimary }]}>Visitor Full Name *</Text>
          <Controller
            control={control}
            name="visitorName"
            render={({ field: { onChange, onBlur, value } }) => (
              <TextInput
                onBlur={onBlur}
                onChangeText={onChange}
                value={value}
                placeholder="e.g. Robert Vance"
                placeholderTextColor={theme.textSecondary}
                style={[
                  globalStyles.input,
                  {
                    backgroundColor: theme.inputBg,
                    borderColor: theme.border,
                    color: theme.textPrimary,
                  },
                ]}
              />
            )}
          />
          {errors.visitorName && <Text style={styles.errorText}>{errors.visitorName.message}</Text>}

          {/* Visitor Company */}
          <Text style={[styles.label, { color: theme.textPrimary, marginTop: 4 }]}>Company / Organization</Text>
          <Controller
            control={control}
            name="visitorCompany"
            render={({ field: { onChange, onBlur, value } }) => (
              <TextInput
                onBlur={onBlur}
                onChangeText={onChange}
                value={value}
                placeholder="e.g. Global Tech Solutions"
                placeholderTextColor={theme.textSecondary}
                style={[
                  globalStyles.input,
                  {
                    backgroundColor: theme.inputBg,
                    borderColor: theme.border,
                    color: theme.textPrimary,
                  },
                ]}
              />
            )}
          />

          {/* Contact Details (Phone & Email) */}
          <View style={styles.row}>
            <View style={{ flex: 1, marginRight: 8 }}>
              <Text style={[styles.label, { color: theme.textPrimary }]}>Phone Number *</Text>
              <Controller
                control={control}
                name="visitorPhone"
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    onBlur={onBlur}
                    onChangeText={onChange}
                    value={value}
                    placeholder="+19876543210"
                    placeholderTextColor={theme.textSecondary}
                    keyboardType="phone-pad"
                    style={[
                      globalStyles.input,
                      {
                        backgroundColor: theme.inputBg,
                        borderColor: theme.border,
                        color: theme.textPrimary,
                      },
                    ]}
                  />
                )}
              />
              {errors.visitorPhone && <Text style={styles.errorText}>{errors.visitorPhone.message}</Text>}
            </View>

            <View style={{ flex: 1, marginLeft: 8 }}>
              <Text style={[styles.label, { color: theme.textPrimary }]}>Email Address *</Text>
              <Controller
                control={control}
                name="visitorEmail"
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    onBlur={onBlur}
                    onChangeText={onChange}
                    value={value}
                    placeholder="visitor@company.com"
                    placeholderTextColor={theme.textSecondary}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    style={[
                      globalStyles.input,
                      {
                        backgroundColor: theme.inputBg,
                        borderColor: theme.border,
                        color: theme.textPrimary,
                      },
                    ]}
                  />
                )}
              />
              {errors.visitorEmail && <Text style={styles.errorText}>{errors.visitorEmail.message}</Text>}
            </View>
          </View>

          {/* Meeting Date Selection */}
          <Text style={[styles.label, { color: theme.textPrimary, marginTop: 4 }]}>
            Preferred Date (YYYY-MM-DD) *
          </Text>
          <TextInput
            value={selectedDate}
            onChangeText={(txt) => {
              setSelectedDate(txt);
              setSelectedSlot(null);
            }}
            placeholder="YYYY-MM-DD"
            placeholderTextColor={theme.textSecondary}
            style={[
              globalStyles.input,
              {
                backgroundColor: theme.inputBg,
                borderColor: theme.border,
                color: theme.textPrimary,
              },
            ]}
          />

          {/* Slot Selection */}
          <SlotPicker
            slots={slotData?.slots || []}
            selectedSlot={selectedSlot}
            onSelectSlot={(slotTime) => setSelectedSlot(slotTime)}
          />
          {errors.meetingTime && <Text style={styles.errorText}>{errors.meetingTime.message}</Text>}

          {/* Purpose */}
          <Text style={[styles.label, { color: theme.textPrimary, marginTop: 8 }]}>Meeting Purpose *</Text>
          <Controller
            control={control}
            name="meetingPurpose"
            render={({ field: { onChange, onBlur, value } }) => (
              <TextInput
                onBlur={onBlur}
                onChangeText={onChange}
                value={value}
                multiline
                numberOfLines={3}
                placeholder="Detail meeting topics, agenda, or department requested..."
                placeholderTextColor={theme.textSecondary}
                style={[
                  globalStyles.input,
                  {
                    backgroundColor: theme.inputBg,
                    borderColor: theme.border,
                    color: theme.textPrimary,
                    height: 80,
                    textAlignVertical: 'top',
                  },
                ]}
              />
            )}
          />
          {errors.meetingPurpose && <Text style={styles.errorText}>{errors.meetingPurpose.message}</Text>}

          {/* Submit Button */}
          <TouchableOpacity
            onPress={handleSubmit(onSubmit)}
            disabled={isLoading}
            style={[globalStyles.buttonPrimary, { marginTop: 12 }]}
          >
            {isLoading ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={globalStyles.buttonText}>Submit Meeting Request 🚀</Text>
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  label: {
    fontSize: 12,
    fontWeight: '700',
    marginBottom: 6,
  },
  errorText: {
    color: COLORS.danger,
    fontSize: 11,
    marginTop: -8,
    marginBottom: 8,
    fontWeight: '600',
  },
  row: {
    flexDirection: 'row',
    marginTop: 4,
  },
});
